import { BrandMark } from './BrandMark';
import { titleBarLogoUrl } from './titleBarLogo';
import { useTranslation } from '@/i18n';
import type { Check, Location } from '@/api/types';

interface CheckHeaderProps {
    location: Location;
    check: Check;
}

/**
 * Who this is, and which check, at the top of every screen in the payment flow.
 *
 * Shared rather than repeated so the guest sees the same identity from the itemisation through to paying —
 * and so the heading rule lives in exactly one place. An earlier version of this markup rendered a bare
 * <img> on a branded location, leaving the page with no heading at all; keeping it here means that cannot
 * come back one screen at a time.
 */
export function CheckHeader({ location, check }: CheckHeaderProps) {
    const { t } = useTranslation();

    /*
     * A check is identified by a table OR by a name - a bar tab, a to-go order, anything not seated. Table
     * wins when both are present: a seated guest recognises their table faster than the name the server typed.
     * When neither exists the check number carries the identity on its own.
     */
    const identifier = check.table ? t('check.table', { table: check.table }) : check.name?.trim() || null;

    return (
        <header className="flex flex-col gap-2.5 px-5 pb-4 pt-9">
            {/*
              * An h1 either way. A heading is how a screen-reader user works out where they are and jumps
              * into a page, and the location name is the logo's alt text - a logo conveys identity, so a
              * screen reader should hear who this is rather than "image". That alt text is also what gives
              * the heading its name.
              *
              * The title bar has a logo of its own where a brand supplied one - see titleBarLogoUrl. Light
              * surfaces only; the dark pair is for when the app honours a dark scheme.
              */}
            <h1 className="text-[1.75rem] font-semibold leading-tight tracking-tight text-balance">
                <BrandMark logoUrl={titleBarLogoUrl(location)} name={location.name} />
            </h1>

            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                {identifier && (
                    <span className="max-w-full truncate rounded-md bg-background px-2 py-0.5 font-medium text-foreground shadow-xs">
                        {identifier}
                    </span>
                )}
                <span className="tabular text-muted-foreground">
                    {t('check.number', { number: check.number })}
                </span>
            </div>
        </header>
    );
}
